// src/modules/care-gaps-2/components/CareGapStatusPieDashboard.jsx
import * as React from "react";
import { Box, Paper, Stack, Typography, Divider, Chip, Grid } from "@mui/material";

import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
} from "recharts";

// ✅ IMPORT DEL JSON (AJUSTA SI TU RUTA REAL ES DIFERENTE)
import CaregapTypeAll from "../DataTest/CaregapTypeAll.json";

// =====================================================
// CONFIG
// =====================================================

// ✅ Medidas “contract / priority” que quieres mostrar SIEMPRE en el reporte
const CONTRACT_MEASURES = [
  { key: "BCS", label: "Breast Cancer Screening", match: ["Breast Cancer Screening"] },
  {
    key: "PHAR",
    label: "Appropriate Testing for Pharyngitis",
    match: ["Appropriate Testing for Pharyngitis"],
  },
  {
    key: "DM_A1C",
    label: "Comprehensive Diabetes Care: HbA1c Good Control",
    // ⚠️ Tu JSON NO trae “Comprehensive Diabetes Care HbA1c Good Control”
    // trae: "Hemoglobin A1C Control for Patients with Diabetes (GSD Measure)"
    match: [
      "Hemoglobin A1C Control for Patients with Diabetes (GSD Measure)",
      "Hemoglobin A1C Control for Patients with Diabetes",
      "Comprehensive Diabetes Care HbA1c Good Control",
    ],
  },
  { key: "COL", label: "Colorectal Cancer Screening", match: ["Colorectal Cancer Screening"] },
  {
    key: "CCS",
    label: "Cervical Cancer Screening",
    match: ["Cervical Cancer Screening, 21-64 years", "Cervical Cancer Screening"],
  },
];

// ✅ OPEN azul con texto blanco / CLOSED oscuro (no rojo) / SUBMITTED ámbar
const STATUS = [
  { key: "OPEN", label: "Open", color: "#1976d2" },
  { key: "CLOSED", label: "Closed", color: "#455a64" },
  { key: "SUBMITTED", label: "Submitted", color: "#f9a825" },
];

// =====================================================
// HELPERS
// =====================================================

const safeNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const normalize = (s) => String(s || "").trim().toLowerCase();

const pct = (num, den) => {
  const n = safeNum(num);
  const d = safeNum(den);
  if (!d) return 0;
  return Math.round((n * 10000) / d) / 100;
};

const formatPct = (v) => `${Number(v || 0).toFixed(2)}%`;

function getColor(statusKey) {
  return STATUS.find((x) => x.key === statusKey)?.color || "#90a4ae";
}

function findMeasureRow(summaryRows, matchList) {
  const rows = Array.isArray(summaryRows) ? summaryRows : [];
  const matchSet = new Set((matchList || []).map((x) => normalize(x)));

  // 1) exact
  const exact = rows.find((r) => matchSet.has(normalize(r?.careGapType)));
  if (exact) return exact;

  // 2) partial contains
  const partial = rows.find((r) => {
    const name = normalize(r?.careGapType);
    for (const m of matchSet) {
      if (m && name.includes(m)) return true;
    }
    return false;
  });

  return partial || null;
}

function buildStatusPie({ open, closed, submitted }) {
  return [
    { key: "OPEN", name: "Open", value: safeNum(open) },
    { key: "CLOSED", name: "Closed", value: safeNum(closed) },
    { key: "SUBMITTED", name: "Submitted", value: safeNum(submitted) },
  ];
}

function sumPie(pieData) {
  return (pieData || []).reduce((acc, x) => acc + safeNum(x.value), 0);
}

function normalizeRow(r) {
  // tu JSON trae open/closed/submitted/total ✅
  // pero “All” trae Open/Close/Submitted con mayúsculas raras ❌
  const t = safeNum(r?.total) || safeNum(r?.open) + safeNum(r?.closed) + safeNum(r?.submitted);

  return {
    careGapType: r?.careGapType,
    total: t,
    open: safeNum(r?.open ?? r?.Open),
    closed: safeNum(r?.closed ?? r?.Close),
    submitted: safeNum(r?.submitted ?? r?.Submitted),
  };
}

// =====================================================
// UI
// =====================================================

function MiniStatusChips({ open, closed, submitted, total }) {
  const t = safeNum(total) || safeNum(open) + safeNum(closed) + safeNum(submitted);

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      <Chip size="small" label={`Total: ${t}`} />
      <Chip
        size="small"
        label={`Open: ${safeNum(open)} (${formatPct(pct(open, t))})`}
        sx={{ bgcolor: `${getColor("OPEN")}22`, border: `1px solid ${getColor("OPEN")}55` }}
      />
      <Chip
        size="small"
        label={`Closed: ${safeNum(closed)} (${formatPct(pct(closed, t))})`}
        sx={{ bgcolor: `${getColor("CLOSED")}22`, border: `1px solid ${getColor("CLOSED")}55` }}
      />
      <Chip
        size="small"
        label={`Submitted: ${safeNum(submitted)} (${formatPct(pct(submitted, t))})`}
        sx={{
          bgcolor: `${getColor("SUBMITTED")}22`,
          border: `1px solid ${getColor("SUBMITTED")}55`,
        }}
      />
    </Stack>
  );
}

function PerformanceBar({ closedPct }) {
  const value = Math.max(0, Math.min(100, Number(closedPct || 0)));

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" color="text.secondary">
        Performance (Closed %)
      </Typography>

      <Box sx={{ mt: 0.5, height: 10, borderRadius: 999, bgcolor: "grey.200", overflow: "hidden" }}>
        <Box sx={{ width: `${value}%`, height: "100%", bgcolor: getColor("CLOSED") }} />
      </Box>

      <Typography variant="caption" sx={{ mt: 0.5, display: "block", fontWeight: 900 }}>
        {formatPct(value)}
      </Typography>
    </Box>
  );
}

function MeasureCard({ title, row }) {
  const open = safeNum(row?.open);
  const closed = safeNum(row?.closed);
  const submitted = safeNum(row?.submitted);
  const total = safeNum(row?.total) || open + closed + submitted;

  const pieData = buildStatusPie({ open, closed, submitted });
  const pieTotal = sumPie(pieData);

  const closedPct = pct(closed, total);
  const missing = !row;

  return (
    <Paper sx={{ p: 2, borderRadius: 3, height: "100%" }}>
      <Stack spacing={1.25}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
            {title}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {missing ? "Not found in current summary (check mapping)" : row?.careGapType || ""}
          </Typography>
        </Box>

        {!missing && (
          <>
            <MiniStatusChips open={open} closed={closed} submitted={submitted} total={total} />
            <PerformanceBar closedPct={closedPct} />
          </>
        )}

        <Divider sx={{ my: 0.5 }} />

        <Box sx={{ width: "100%", height: 220, opacity: missing ? 0.5 : 1 }}>
          <ResponsiveContainer>
            <PieChart>
              <RechartsTooltip
                formatter={(value, name) => {
                  const v = safeNum(value);
                  const pctValue = pieTotal > 0 ? pct(v, pieTotal) : 0;
                  return [`${v} (${formatPct(pctValue)})`, name];
                }}
              />
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                innerRadius={42}
                paddingAngle={2}
                isAnimationActive={false}
              >
                {pieData.map((entry) => (
                  <Cell key={entry.key} fill={getColor(entry.key)} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </Box>
      </Stack>
    </Paper>
  );
}

// =====================================================
// MAIN
// =====================================================

/**
 * Puedes usarlo de 2 maneras:
 *
 * 1) SIN props -> usa el JSON de DataTest automáticamente
 *    <CareGapStatusPieDashboard />
 *
 * 2) CON props summary (cuando ya venga del backend)
 *    <CareGapStatusPieDashboard summary={dataFromApi} />
 */
export default function CareGapStatusPieDashboard({ summary }) {
  // ✅ si no pasas summary, usa el JSON
  const sourceRows = React.useMemo(() => {
    const base = Array.isArray(summary) && summary.length ? summary : CaregapTypeAll;
    return (base || []).map(normalizeRow);
  }, [summary]);

  // Remover “All”
  const cleanedRows = React.useMemo(
    () => sourceRows.filter((x) => normalize(x?.careGapType) !== "all"),
    [sourceRows]
  );

  const mapped = React.useMemo(() => {
    return CONTRACT_MEASURES.map((m) => ({
      ...m,
      row: findMeasureRow(cleanedRows, m.match),
    }));
  }, [cleanedRows]);

  const overall = React.useMemo(() => {
    const acc = { open: 0, closed: 0, submitted: 0, total: 0 };
    for (const r of cleanedRows) {
      acc.open += safeNum(r?.open);
      acc.closed += safeNum(r?.closed);
      acc.submitted += safeNum(r?.submitted);
      acc.total += safeNum(r?.total) || safeNum(r?.open) + safeNum(r?.closed) + safeNum(r?.submitted);
    }
    return acc;
  }, [cleanedRows]);

  const overallClosedPct = pct(overall.closed, overall.total);

  // si por alguna razón no hay data
  const empty = cleanedRows.length === 0;

  return (
    <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" fontWeight={900}>
            HEDIS Contract Measures Performance
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            Based on Care Gap Type totals (Open / Closed / Submitted).
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label="OPEN" sx={{ bgcolor: getColor("OPEN"), color: "common.white", fontWeight: 900 }} />
          <Chip
            label="CLOSED"
            sx={{ bgcolor: getColor("CLOSED"), color: "common.white", fontWeight: 900 }}
          />
          <Chip
            label="SUBMITTED"
            sx={{ bgcolor: getColor("SUBMITTED"), color: "common.white", fontWeight: 900 }}
          />
          <Box sx={{ flexGrow: 1 }} />
          <Chip variant="outlined" label={`Overall Closed %: ${formatPct(overallClosedPct)}`} sx={{ fontWeight: 900 }} />
        </Stack>

        <Divider />

        {empty ? (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No data found. Check the JSON import path (case-sensitive) and that the file is included by Vite.
            </Typography>
          </Paper>
        ) : (
          <>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                Overall (All Measures)
              </Typography>
              <MiniStatusChips
                open={overall.open}
                closed={overall.closed}
                submitted={overall.submitted}
                total={overall.total}
              />
              <PerformanceBar closedPct={overallClosedPct} />
            </Paper>

            <Grid container spacing={2}>
              {mapped.map((m) => (
                <Grid item xs={12} md={6} lg={4} key={m.key}>
                  <MeasureCard title={m.label} row={m.row} />
                </Grid>
              ))}
            </Grid>

            {mapped.some((x) => !x.row) && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 3,
                  borderColor: "warning.light",
                  bgcolor: "warning.light" + "22",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                  Mapping note
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Some contract measures were not found in the JSON summary. This is usually a naming mismatch.
                  Update the <b>CONTRACT_MEASURES</b> match list to align with your strings.
                </Typography>
              </Paper>
            )}
          </>
        )}
      </Stack>
    </Paper>
  );
}
